import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";

import { DemandsService } from "../authentication/services/demands.service";
import { CongratsComponent, CongratsType } from "../shared/components/congrats/congrats.component";
import { DialogService } from "../shared/components/dialog/dialog.service";
import { EntityFormGroup } from "../shared/types/entity-form-group";
import { Talker } from "../users/models/talker.entity";
import { ProfileService } from '../users/services/profile.service';
import { Role } from "../users/models/users.entity";

export enum QuestionType {
  TRAINS = 'trains',
  SHERPA = 'sherpa',
  GENERAL = 'general',
  TECHNICAL = 'technical',
  OTHER = 'other',
  UNITS = 'units',
  SUBSCRIPTION_PLAN = 'subscription_plan',
  CONTENT = 'content',
}

export type QuestionForm = {
  subject: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  content: string;
}

export type QuestionRequestPayload = Pick<QuestionForm, "subject" | "content">;

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  styleUrls: [ './questions.component.scss' ]
})
export class QuestionsComponent implements OnInit, OnDestroy {

  questionForm: EntityFormGroup<QuestionForm>;

  isPricingRequest = false;

  requestedPlanLabel?: string;


  private subjectRecord: Record<QuestionType, string> = {
    [QuestionType.TRAINS]: 'Les parcours',
    [QuestionType.SHERPA]: 'Les Sherpas',
    [QuestionType.UNITS]: 'Les crédits',
    [QuestionType.SUBSCRIPTION_PLAN]: 'Les abonnements et tarifs',
    [QuestionType.TECHNICAL]: 'Le fonctionnement de la plateforme / un problème technique',
    [QuestionType.CONTENT]: 'Les ressources (vidéos, podcasts, cartes ressources, …)',
    [QuestionType.GENERAL]: 'L’entreprise We talk',
    [QuestionType.OTHER]: 'Autre sujet',
  };

  profile: any;

  apiCalling = false;


  private destroy$ = new Subject<void>();

  // private readonly tracker = inject(MatomoTracker);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly demandsService: DemandsService,
    private readonly dialog: DialogService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly profileService: ProfileService
  ) {
    // this.tracker.trackPageView('Contact');
    this.questionForm = this.formBuilder.group({
      subject: new FormControl('', { validators: Validators.required, nonNullable: true }),
      companyName: new FormControl('', { nonNullable: true }),
      contactName: new FormControl('', { nonNullable: true }),
      contactEmail: new FormControl('', { validators: Validators.email, nonNullable: true }),
      contactPhone: new FormControl('', { nonNullable: true }),
      content: new FormControl('', { validators: Validators.required, nonNullable: true })
    });
  }

  get subjects(): string[] {
    return Object.values(this.subjectRecord);
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.applyQueryParams(params);
      });

    this.profileService.getMyProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: profile => {
          this.profile = profile;
          this.prefillContactFields();
        },
        error: () => undefined,
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  sendEmail() {
    this.apiCalling = true;
    this.demandsService.sendQuestion({
      subject: Object.values(QuestionType).find(type => this.subjectRecord[type] === this.questionForm.controls.subject.value) ?? 'other',
      content: this.buildQuestionContent()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.apiCalling = false;
        this.dialog.open(CongratsComponent, {
          title: `Merci pour votre sollicitation !`,
          message: this.isPricingRequest
            ? 'Nous allons vous recontacter dans les plus brefs délais avec la proposition adaptée.'
            : 'Nous allons vous répondre dans les plus brefs délais sur votre adresse mail.',
          type: CongratsType.POST_NEW_SHERPA
        }).subscribe(() => {
          this.router.navigate([ 'home' ]);
        });
      });

  }

  private applyQueryParams(params: ParamMap): void {
    const source = params.get('source');
    const subject = params.get('subject');
    const plan = params.get('plan');

    this.isPricingRequest = source === 'company-offers-2026';
    this.requestedPlanLabel = this.getRequestedPlanLabel(plan);

    this.togglePricingValidators(this.isPricingRequest);

    if (subject && this.subjectRecord[subject as QuestionType]) {
      this.questionForm.controls.subject.setValue(this.subjectRecord[subject as QuestionType]);
    }

    if (this.isPricingRequest && !this.questionForm.controls.content.value.trim()) {
      this.questionForm.controls.content.setValue(this.getPricingTemplateMessage());
    }
  }

  private togglePricingValidators(isPricingRequest: boolean): void {
    const requiredTextValidators = isPricingRequest ? [ Validators.required ] : [];
    const emailValidators = isPricingRequest ? [ Validators.required, Validators.email ] : [ Validators.email ];

    this.questionForm.controls.companyName.setValidators(requiredTextValidators);
    this.questionForm.controls.contactName.setValidators(requiredTextValidators);
    this.questionForm.controls.contactEmail.setValidators(emailValidators);
    this.questionForm.controls.contactPhone.setValidators(requiredTextValidators);

    this.questionForm.controls.companyName.updateValueAndValidity({ emitEvent: false });
    this.questionForm.controls.contactName.updateValueAndValidity({ emitEvent: false });
    this.questionForm.controls.contactEmail.updateValueAndValidity({ emitEvent: false });
    this.questionForm.controls.contactPhone.updateValueAndValidity({ emitEvent: false });
  }

  private prefillContactFields(): void {
    if (!this.profile) {
      return;
    }

    const organizationName = this.getOrganizationNameFromProfile();
    const contactName = [ this.profile.firstName, this.profile.lastName ].filter(Boolean).join(' ');

    if (!this.questionForm.controls.companyName.value && organizationName) {
      this.questionForm.controls.companyName.setValue(organizationName);
    }

    if (!this.questionForm.controls.contactName.value && contactName) {
      this.questionForm.controls.contactName.setValue(contactName);
    }

    if (!this.questionForm.controls.contactEmail.value && this.profile.email) {
      this.questionForm.controls.contactEmail.setValue(this.profile.email);
    }

    if (!this.questionForm.controls.contactPhone.value && this.profile.phoneNumber) {
      this.questionForm.controls.contactPhone.setValue(this.profile.phoneNumber);
    }
  }

  private getOrganizationNameFromProfile(): string | undefined {
    if (this.profile?.role !== Role.Talker) {
      return undefined;
    }

    const talkerProfile = this.profile as Talker;
    return talkerProfile.organization?.name
      ?? talkerProfile.organizationAssociations?.find(association => !!association.organization)?.organization?.name;
  }

  private getRequestedPlanLabel(plan?: string | null): string | undefined {
    switch (plan) {
      case 'wetalk':
        return 'We talk Basic';
      case 'wetalk-plus':
        return 'We talk Plus';
      case 'wetalk-coach':
        return 'We talk coach';
      default:
        return undefined;
    }
  }

  private getPricingTemplateMessage(): string {
    const planLine = this.requestedPlanLabel ? `Je souhaite recevoir la proposition pour l'offre ${ this.requestedPlanLabel }.` : 'Je souhaite recevoir la proposition détaillée des offres entreprise 2026.';
    return `${ planLine }\n\nMerci de me recontacter pour préciser le nombre de collaborateurs, le besoin de pilotage et le niveau d'accompagnement attendu.`;
  }

  private buildQuestionContent(): string {
    const message = this.questionForm.controls.content.value.trim();

    if (!this.isPricingRequest) {
      return message;
    }

    const companyDetails = [
      message,
      '',
      'Coordonnees entreprise :',
      `Entreprise : ${ this.questionForm.controls.companyName.value }`,
      `Contact : ${ this.questionForm.controls.contactName.value }`,
      `Email : ${ this.questionForm.controls.contactEmail.value }`,
      `Telephone : ${ this.questionForm.controls.contactPhone.value }`,
      this.requestedPlanLabel ? `Offre demandee : ${ this.requestedPlanLabel }` : undefined,
    ].filter((line): line is string => !!line);

    return companyDetails.join('\n');
  }
}
